/**
 * Présence locale pour le TÉLÉPHONE MANUEL (softphone), côté client.
 *
 * IMPORTANT : distinct de la présence locale des campagnes Power Dialer, qui
 * a son propre pool de numéros et sa logique serveur (sticky/pin/cap/pool via
 * resolve_caller_id). Ici on ne travaille QUE sur les numéros perso de l'agent
 * (availableCallerIds) et on ne touche à aucun concept de campagne.
 *
 * Principe : au moment de l'appel, on choisit le numéro de l'agent dont
 * l'indicatif régional (area code NANP) correspond à celui du numéro composé.
 * Sans correspondance → repli : préféré → principal → premier.
 */
import type { AvailableNumber } from '@/types/agents'

/** Valeur sentinelle stockée quand l'agent choisit le mode Automatique. */
export const AUTO_CALLER_ID = 'auto'

/**
 * Extrait l'indicatif régional NANP (3 chiffres) d'un numéro. Retourne null
 * pour un numéro hors NANP (ex. +48…) ou incomplet.
 */
export function extractAreaCode(phone: string | null | undefined): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1, 4)
  if (digits.length === 10) return digits.slice(0, 3)
  // +1AAA… partiel : dès qu'on a l'indicatif complet après le 1.
  if (digits.length >= 4 && digits.startsWith('1')) return digits.slice(1, 4)
  return null
}

/** Normalise un numéro en E.164 (NANP par défaut). Clé stable du sticky. */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  if (digits.length === 10) return `+1${digits}`
  return `+${digits}`
}

type Source = 'sticky' | 'match' | 'preferred' | 'fallback' | 'none'

export interface AutoResolution {
  /** Numéro effectivement choisi (null si l'agent n'a aucun numéro). */
  number: string | null
  /** D'où vient le choix (utile pour l'UI / debug). */
  source: Source
}

export interface AutoResolveOptions {
  /** Numéro étoilé (préféré) de l'agent — défaut ET repli. */
  preferredCallerId?: string | null
  /** Dernier numéro utilisé pour ce contact en mode auto (sticky backend). */
  stickyCallerId?: string | null
}

/**
 * Résout le caller ID en mode Automatique pour un numéro composé.
 * Ne dépend QUE des numéros de l'agent — aucune notion de campagne.
 *
 * Ordre : sticky (si toujours disponible) → match d'indicatif → numéro étoilé.
 * Le numéro « main » de la company n'intervient jamais comme défaut.
 */
export function resolveAutoCallerId(
  dialedNumber: string | null | undefined,
  numbers: AvailableNumber[],
  opts: AutoResolveOptions = {},
): AutoResolution {
  if (numbers.length === 0) return { number: null, source: 'none' }
  const { preferredCallerId, stickyCallerId } = opts

  // 1. Sticky : on réutilise le dernier numéro présenté à ce contact, à
  //    condition qu'il fasse TOUJOURS partie des numéros dispo de l'agent.
  if (
    stickyCallerId &&
    numbers.some((n) => n.phone_number === stickyCallerId)
  ) {
    return { number: stickyCallerId, source: 'sticky' }
  }

  // 2. Match d'indicatif régional.
  const targetArea = extractAreaCode(dialedNumber)
  if (targetArea) {
    const match = numbers.find(
      (n) => extractAreaCode(n.phone_number) === targetArea,
    )
    if (match) return { number: match.phone_number, source: 'match' }
  }

  // 3. Repli : numéro étoilé (préféré). Jamais le « main » de la company.
  if (preferredCallerId) {
    const star = numbers.find((n) => n.phone_number === preferredCallerId)
    if (star) return { number: star.phone_number, source: 'preferred' }
  }
  return { number: numbers[0].phone_number, source: 'fallback' }
}
